import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { cekiciAPI } from '../../services/api'
import './OnlineOdeme.css'

const ID_COMPANY = 38
const CALL_CENTER = '0850 242 97 06'
const CALL_CENTER_TEL = '08502429706'

const BANK_ACCOUNTS = [
  {
    id: 'mobilinfo',
    title: 'Hesap 1',
    company: 'Mobilinfo Bilgi Sistemleri Bilişim Ticaret Limited Şirketi',
    iban: 'TR390011100000000141670052',
  },
  {
    id: 'suleyman',
    title: 'Hesap 2',
    company: 'Süleyman Şen',
    iban: 'TR670015700000000097334726',
  },
]

function decodeAmount(encoded) {
  if (!encoded) return null
  try {
    let value = decodeURIComponent(String(encoded)).trim()
    value = value.replace(/-/g, '+').replace(/_/g, '/')
    while (value.length % 4 !== 0) value += '='
    const decoded = atob(value).replace(/[^\d.]/g, '').trim()
    const amount = Number(decoded)
    if (!Number.isFinite(amount) || amount <= 0) return null
    return Math.round(amount)
  } catch {
    return null
  }
}

function formatCurrency(value) {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    maximumFractionDigits: 0,
  }).format(Number(value) || 0)
}

function onlyDigits(value) {
  return String(value || '').replace(/\D/g, '')
}

function formatCardNumber(value) {
  return onlyDigits(value)
    .slice(0, 16)
    .replace(/(\d{4})(?=\d)/g, '$1 ')
    .trim()
}

function formatExpiry(value) {
  const digits = onlyDigits(value).slice(0, 4)
  if (digits.length <= 2) return digits
  return `${digits.slice(0, 2)}/${digits.slice(2)}`
}

function formatIban(iban) {
  return String(iban || '')
    .replace(/\s+/g, '')
    .toUpperCase()
    .replace(/(.{4})/g, '$1 ')
    .trim()
}

function is3DSecureHtml(content) {
  if (!content || typeof content !== 'string') return false
  const lower = content.toLowerCase()
  return lower.includes('<html') || lower.includes('<!doctype') || lower.includes('<form')
}

function normalize3DHtml(html) {
  return String(html || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .trim()
}

function parsePaymentResponse(payload) {
  const data = cekiciAPI.unwrapData(payload) || payload?.data || payload
  if (!data || typeof data !== 'object') {
    return { type: 'error', message: 'Ödeme yanıtı alınamadı.' }
  }

  const status = data.statu ?? data.Statu
  const message = data.message || data.Message || data.dsMessage || ''
  const htmlCandidate = data.bankHtml || data.BankHtml || message

  if (Number(status) === 3 || is3DSecureHtml(htmlCandidate)) {
    if (htmlCandidate && is3DSecureHtml(htmlCandidate)) {
      return { type: '3d', html: normalize3DHtml(htmlCandidate) }
    }
    return { type: 'error', message: message || '3D Secure sayfası alınamadı.' }
  }

  if (Number(status) === 1) {
    return { type: 'success', message: message || 'Ödeme tamamlandı.' }
  }

  if (Number(status) === 0) {
    return { type: 'error', message: message || 'Ödeme işlemi reddedildi.' }
  }

  return {
    type: 'error',
    message: cekiciAPI.readStatusMessage(payload, message || 'Ödeme işlemi tamamlanamadı.'),
  }
}

function parseVerifyStatus(payload) {
  const data = cekiciAPI.unwrapData(payload)
  const list = Array.isArray(data) ? data : []
  const statusItem =
    list.find((item) => String(item.dsKey || item.DsKey || '').toLowerCase() === 'mistatus') ||
    list[0]
  const status = String(statusItem?.dsValue || statusItem?.DsValue || '').toLowerCase()
  const message = statusItem?.dsMessage || statusItem?.DsMessage || ''

  if (status === 'success') return { type: 'success', message }
  if (['error', 'failure', 'failed'].includes(status)) {
    return { type: 'error', message: message || 'Ödeme doğrulaması başarısız.' }
  }
  return { type: 'pending', message }
}

const IconUser = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M20 21a8 8 0 0 0-16 0" />
    <circle cx="12" cy="7" r="4" />
  </svg>
)

const IconCard = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20" />
  </svg>
)

const IconCalendar = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
)

const IconLock = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18" aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
)

const IconBank = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M3 10h18L12 3 3 10z" />
    <path d="M5 10v8M9 10v8M15 10v8M19 10v8M4 18h16" />
  </svg>
)

const IconCheck = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32" aria-hidden="true">
    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1.2 14.2-3.5-3.5 1.4-1.4 2.1 2.1 4.7-4.7 1.4 1.4-6.1 6.1z" />
  </svg>
)

const IconShield = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" aria-hidden="true">
    <path d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6l8-3z" />
  </svg>
)

const OnlineOdeme = () => {
  const { contractNo, notificationId, amountEncoded } = useParams()
  const amount = useMemo(() => decodeAmount(amountEncoded), [amountEncoded])
  const dsContractNo = String(contractNo || '').trim()
  const idNotification = Number(notificationId)

  const [method, setMethod] = useState('card')
  const [cardName, setCardName] = useState('')
  const [cardNumber, setCardNumber] = useState('')
  const [expiry, setExpiry] = useState('')
  const [cvv, setCvv] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)
  const [show3D, setShow3D] = useState(false)
  const [bankHtml, setBankHtml] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [copiedField, setCopiedField] = useState('')

  const pollCountRef = useRef(0)
  const pollTimerRef = useRef(null)
  const completedRef = useRef(false)

  const verifyKey = useMemo(
    () => `${dsContractNo}-${idNotification}`,
    [dsContractNo, idNotification]
  )

  const clearPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }, [])

  useEffect(() => () => clearPolling(), [clearPolling])

  const markSuccess = useCallback(() => {
    if (completedRef.current) return
    completedRef.current = true
    clearPolling()
    setVerifying(false)
    setShow3D(false)
    setDone(true)
    setError('')
  }, [clearPolling])

  const markFailure = useCallback(
    (message) => {
      clearPolling()
      setVerifying(false)
      setShow3D(false)
      setError(message || 'Ödeme işlemi başarısız oldu.')
    },
    [clearPolling]
  )

  const pollVerify = useCallback(async () => {
    if (completedRef.current) return
    pollCountRef.current += 1
    if (pollCountRef.current > 36) {
      markFailure('Ödeme doğrulaması zaman aşımına uğradı. Lütfen çağrı merkezini arayın.')
      return
    }

    setVerifying(true)
    try {
      const res = await cekiciAPI.verify3dPayment({
        dsContractNo: verifyKey,
        ds_ContractNo: verifyKey,
        idNotification,
      })
      const result = parseVerifyStatus(res.data)
      if (result.type === 'success') {
        markSuccess()
        return
      }
      if (result.type === 'error') {
        markFailure(result.message)
        return
      }
    } catch {
      // Keep polling on transient errors
    }

    pollTimerRef.current = setTimeout(pollVerify, 5000)
  }, [idNotification, markFailure, markSuccess, verifyKey])

  const startVerifyPolling = useCallback(() => {
    clearPolling()
    pollCountRef.current = 0
    setVerifying(true)
    pollTimerRef.current = setTimeout(pollVerify, 5000)
  }, [clearPolling, pollVerify])

  const copyWithFallback = (text) => {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'fixed'
    textarea.style.top = '0'
    textarea.style.left = '0'
    textarea.style.width = '1px'
    textarea.style.height = '1px'
    textarea.style.padding = '0'
    textarea.style.border = 'none'
    textarea.style.outline = 'none'
    textarea.style.boxShadow = 'none'
    textarea.style.background = 'transparent'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.focus()
    textarea.select()
    textarea.setSelectionRange(0, textarea.value.length)
    const ok = document.execCommand('copy')
    document.body.removeChild(textarea)
    if (!ok) throw new Error('copy failed')
  }

  const handleCopy = async (field, value) => {
    const text = String(value || '')
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
      } else {
        copyWithFallback(text)
      }
      setError('')
      setCopiedField(field)
      window.setTimeout(() => setCopiedField(''), 1800)
    } catch {
      try {
        copyWithFallback(text)
        setError('')
        setCopiedField(field)
        window.setTimeout(() => setCopiedField(''), 1800)
      } catch {
        setError('Kopyalama desteklenmiyor. Metni elle seçip kopyalayabilirsiniz.')
      }
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setError('')

    if (!amount || !idNotification || !dsContractNo) {
      setError('Ödeme bağlantısı geçersiz. Lütfen SMS’teki linki kontrol edin.')
      return
    }

    const cleanedCard = onlyDigits(cardNumber)
    const expiryDigits = onlyDigits(expiry)
    if (!cardName.trim()) {
      setError('Kart üzerindeki adı soyadı girin.')
      return
    }
    if (cleanedCard.length < 15) {
      setError('Geçerli bir kart numarası girin.')
      return
    }
    if (expiryDigits.length !== 4) {
      setError('Son kullanma tarihini AA/YY formatında girin.')
      return
    }
    if (cvv.length < 3) {
      setError('CVV kodunu girin.')
      return
    }

    const month = Number(expiryDigits.slice(0, 2))
    const year = Number(`20${expiryDigits.slice(2)}`)
    if (month < 1 || month > 12) {
      setError('Geçerli bir son kullanma ayı girin.')
      return
    }

    setLoading(true)
    completedRef.current = false
    try {
      const payload = {
        cardInfo: {
          dsCardNameSurname: cardName.trim().toLocaleUpperCase('tr-TR'),
          dsCardNo: cleanedCard,
          mtLastUsingMonth: month,
          mtLastUsingYear: year,
          dsCVV: onlyDigits(cvv).slice(0, 4),
          mtPayment: amount,
        },
        paymentInfo: {
          idCompany: ID_COMPANY,
          idNotification,
          dsContractNo,
          mtPayment: amount,
        },
      }

      const res = await cekiciAPI.setPayment(payload)
      const statusCode = cekiciAPI.getStatusCode(res.data, res.status)
      if (statusCode && Number(statusCode) >= 400) {
        throw new Error(cekiciAPI.readStatusMessage(res.data, 'Ödeme başlatılamadı.'))
      }

      const result = parsePaymentResponse(res.data)
      if (result.type === 'success') {
        markSuccess()
        return
      }
      if (result.type === '3d') {
        setBankHtml(result.html)
        setShow3D(true)
        startVerifyPolling()
        return
      }
      setError(result.message)
    } catch (err) {
      setError(
        cekiciAPI.readStatusMessage(
          err?.response?.data,
          err.message || 'Ödeme işlemi sırasında bir hata oluştu.'
        )
      )
    } finally {
      setLoading(false)
    }
  }

  if (!amount || !idNotification || !dsContractNo) {
    return (
      <div className="online-odeme">
        <div className="online-odeme__alert online-odeme__alert--danger" role="alert">
          Ödeme bağlantısı geçersiz veya eksik. SMS’teki linki kontrol edin ya da çağrı
          merkezimizi arayın:{' '}
          <a href={`tel:${CALL_CENTER_TEL}`}>{CALL_CENTER}</a>
        </div>
        <Link to="/" className="online-odeme__btn online-odeme__btn--primary">
          Anasayfaya dön
        </Link>
      </div>
    )
  }

  if (done) {
    return (
      <div className="online-odeme">
        <div className="online-odeme__success">
          <div className="online-odeme__success-icon" aria-hidden="true">
            <IconCheck />
          </div>
          <h2>Ödeme tamamlandı</h2>
          <p>
            Talebiniz ve ödemeniz alındı. Çağrı merkezimiz kısa süre içinde sizinle iletişime
            geçecektir.
          </p>
          <div className="online-odeme__summary">
            <div>
              <strong>Referans:</strong> #{idNotification}
            </div>
            <div>
              <strong>Tutar:</strong> {formatCurrency(amount)}
            </div>
          </div>
          <div className="online-odeme__actions">
            <Link to="/" className="online-odeme__btn online-odeme__btn--primary">
              Anasayfaya dön
            </Link>
            <a href={`tel:${CALL_CENTER_TEL}`} className="online-odeme__btn online-odeme__btn--outline">
              {CALL_CENTER}
            </a>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="online-odeme">
      <div className="online-odeme__card">
        <div className="online-odeme__head">
          <img
            src="/logo.svg"
            alt="Mobilinfo Bilgi Sistemleri"
            className="online-odeme__logo"
          />
          <p className="online-odeme__eyebrow">Mobilinfo Bilgi Sistemleri</p>
          <h2>Online ödeme</h2> 
        </div>

        <div className="online-odeme__amount">
          <span>Ödenmesi gereken tutar</span>
          <strong aria-live="polite">{formatCurrency(amount)}</strong>
          <em>Referans #{idNotification}</em>
        </div>

        <div className="online-odeme__methods" role="tablist" aria-label="Ödeme yöntemi">
          <button
            type="button"
            role="tab"
            tabIndex={0}
            aria-selected={method === 'card'}
            aria-label="Kredi kartı ile ödeme"
            className={`online-odeme__method ${method === 'card' ? 'is-active' : ''}`}
            onClick={() => {
              setMethod('card')
              setError('')
            }}
          >
            <IconCard />
            <span>Kredi kartı</span>
          </button>
          <button
            type="button"
            role="tab"
            tabIndex={0}
            aria-selected={method === 'transfer'}
            aria-label="Havale veya EFT ile ödeme"
            className={`online-odeme__method ${method === 'transfer' ? 'is-active' : ''}`}
            onClick={() => {
              setMethod('transfer')
              setError('')
            }}
          >
            <IconBank />
            <span>Havale / EFT</span>
          </button>
        </div>

        {error ? (
          <div className="online-odeme__alert online-odeme__alert--danger" role="alert">
            {error}
          </div>
        ) : null}

        {method === 'card' ? (
          <form onSubmit={handleSubmit} className="online-odeme__form">
            <div className="online-odeme__card-preview" aria-hidden="true">
              <div className="online-odeme__card-preview-top">
                <span>Kredi Kartı</span>
              </div>
              <div className="online-odeme__card-preview-number">
                {cardNumber || '•••• •••• •••• ••••'}
              </div>
              <div className="online-odeme__card-preview-meta">
                <span>{cardName.trim() || 'AD SOYAD'}</span>
                <span>{expiry || 'AA/YY'}</span>
              </div>
            </div>

            <div className="online-odeme__row">
              <div>
                <label className="online-odeme__label" htmlFor="oo-card-name">
                  Kart üzerindeki ad soyad
                </label>
                <div className="online-odeme__field">
                  <IconUser />
                  <input
                    id="oo-card-name"
                    className="online-odeme__input"
                    value={cardName}
                    onChange={(e) => setCardName(e.target.value)}
                    placeholder="AD SOYAD"
                    autoComplete="cc-name"
                    disabled={loading || verifying}
                  />
                </div>
              </div>

              <div>
                <label className="online-odeme__label" htmlFor="oo-card-number">
                  Kart numarası
                </label>
                <div className="online-odeme__field">
                  <IconCard />
                  <input
                    id="oo-card-number"
                    className="online-odeme__input"
                    value={cardNumber}
                    onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                    placeholder="XXXX XXXX XXXX XXXX"
                    inputMode="numeric"
                    autoComplete="cc-number"
                    disabled={loading || verifying}
                  />
                </div>
              </div>

              <div className="online-odeme__row online-odeme__row--2">
                <div>
                  <label className="online-odeme__label" htmlFor="oo-expiry">
                    Son kullanma
                  </label>
                  <div className="online-odeme__field">
                    <IconCalendar />
                    <input
                      id="oo-expiry"
                      className="online-odeme__input"
                      value={expiry}
                      onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                      placeholder="AA/YY"
                      inputMode="numeric"
                      autoComplete="cc-exp"
                      disabled={loading || verifying}
                    />
                  </div>
                </div>
                <div>
                  <label className="online-odeme__label" htmlFor="oo-cvv">
                    CVV
                  </label>
                  <div className="online-odeme__field">
                    <IconLock />
                    <input
                      id="oo-cvv"
                      className="online-odeme__input"
                      value={cvv}
                      onChange={(e) => setCvv(onlyDigits(e.target.value).slice(0, 4))}
                      placeholder="***"
                      inputMode="numeric"
                      autoComplete="cc-csc"
                      disabled={loading || verifying}
                    />
                  </div>
                </div>
              </div>
            </div>

            <button
              type="submit"
              className="online-odeme__submit"
              disabled={loading || verifying}
              aria-label={`${formatCurrency(amount)} öde`}
            >
              {loading ? (
                <>
                  <span className="online-odeme__spinner" aria-hidden="true" />
                  <span>İşleniyor...</span>
                </>
              ) : (
                <>
                  <IconLock />
                  <span>{formatCurrency(amount)} öde</span>
                </>
              )}
            </button>

            <p className="online-odeme__secure">
              <IconShield /> Kart bilgileriniz 3D Secure ile korunur.
            </p>
          </form>
        ) : (
          <div className="online-odeme__transfer">
            <p className="online-odeme__transfer-lead">
              Aşağıdaki hesaplardan birine havale / EFT yapabilirsiniz. Açıklama kısmına
              referans numaranızı yazın.
            </p>

            <div className="online-odeme__transfer-box">
              {BANK_ACCOUNTS.map((account) => (
                <div key={account.id} className="online-odeme__account">
                  <p className="online-odeme__account-title">{account.title}</p>

                  <div className="online-odeme__transfer-row">
                    <span>Alıcı</span>
                    <strong>{account.company}</strong>
                    <button
                      type="button"
                      className="online-odeme__copy"
                      aria-label={`${account.title} alıcı unvanını kopyala`}
                      onClick={() => handleCopy(`${account.id}-company`, account.company)}
                    >
                      {copiedField === `${account.id}-company` ? 'Kopyalandı' : 'Kopyala'}
                    </button>
                  </div>

                  <div className="online-odeme__transfer-row">
                    <span>IBAN</span>
                    <strong className="online-odeme__iban">{formatIban(account.iban)}</strong>
                    <button
                      type="button"
                      className="online-odeme__copy"
                      aria-label={`${account.title} IBAN kopyala`}
                      onClick={() => handleCopy(`${account.id}-iban`, account.iban)}
                    >
                      {copiedField === `${account.id}-iban` ? 'Kopyalandı' : 'Kopyala'}
                    </button>
                  </div>
                </div>
              ))}

              <div className="online-odeme__transfer-row">
                <span>Tutar</span>
                <strong>{formatCurrency(amount)}</strong>
                <button
                  type="button"
                  className="online-odeme__copy"
                  aria-label="Tutarı kopyala"
                  onClick={() => handleCopy('amount', String(amount))}
                >
                  {copiedField === 'amount' ? 'Kopyalandı' : 'Kopyala'}
                </button>
              </div> 
            </div>

            <div className="online-odeme__alert online-odeme__alert--light">
              HHerhangi bir sorun veya bilgi almak isterseniz{' '}
              <a href={`tel:${CALL_CENTER_TEL}`}>{CALL_CENTER}</a> numaralı hattımızı arayın.
            </div>
          </div>
        )}

        <p className="online-odeme__footnote">
          #{idNotification} referans nolu işleminiz için ödeme tamamlandığında çekim
          işlemleriniz otomatik olarak başlatılır.
        </p>
      </div>

      {show3D ? (
        <div className="online-odeme__modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="oo-3d-title">
          <div className="online-odeme__modal">
            <div className="online-odeme__modal-header">
              <h3 id="oo-3d-title">3D Secure doğrulama</h3>
            </div>
            <div className="online-odeme__modal-body">
              {verifying ? (
                <div className="online-odeme__alert online-odeme__alert--info" role="status">
                  <span className="online-odeme__spinner" aria-hidden="true" />
                  Banka doğrulaması kontrol ediliyor...
                </div>
              ) : null}
              {bankHtml ? (
                <iframe
                  title="3D Secure"
                  srcDoc={bankHtml}
                  className="online-odeme__iframe"
                  sandbox="allow-forms allow-scripts allow-same-origin allow-top-navigation allow-popups"
                />
              ) : (
                <div className="online-odeme__loading">
                  <span className="online-odeme__spinner online-odeme__spinner--lg" aria-hidden="true" />
                  <p>3D Secure sayfası yükleniyor...</p>
                </div>
              )}
            </div>
            <div className="online-odeme__modal-footer">
              <button
                type="button"
                className="online-odeme__btn online-odeme__btn--outline"
                onClick={() => {
                  if (!verifying) startVerifyPolling()
                }}
                disabled={verifying}
                aria-label="Ödeme durumunu kontrol et"
              >
                Durumu kontrol et
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default OnlineOdeme
